import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  DialogActions as MuiDialogActions,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CustomAutoComplete,
  CustomButton,
  CustomSelect,
  CustomTextArea,
  CustomTextField,
  DialogBody,
  DialogContainer,
  DialogTitle,
} from '../../../common/components';
import {
  useCreateNotificationEvent,
  useNotificationRules,
  useRunNotificationRule,
} from '../../../hooks/useNotifications';
import { useUsers } from '../../../hooks/useUsers';

export const NotificationsOperationsDialog = ({ open, onClose, initialRuleUuid = '' }) => {
  const { data: rulesData } = useNotificationRules({ target_app_module: 'habitat_99' });
  const { data: usersData = [], isLoading: usersLoading } = useUsers();
  const createEvent = useCreateNotificationEvent();
  const runRule = useRunNotificationRule();
  const rules = rulesData?.results || [];
  const users = useMemo(
    () => (Array.isArray(usersData) ? usersData : usersData?.results || []),
    [usersData]
  );
  const [manualRuleUuid, setManualRuleUuid] = useState('');
  const [manualDryRun, setManualDryRun] = useState(true);
  const [selectedRecipientUuid, setSelectedRecipientUuid] = useState('');
  const [recipientUserUuids, setRecipientUserUuids] = useState([]);
  const [form, setForm] = useState({
    uuid_rule: '',
    title: '',
    message: '',
    dedupe_key: '',
    contract_number: '',
  });

  const selectedRule = rules.find((ruleItem) => ruleItem.uuid === form.uuid_rule);
  const manualRule = rules.find((ruleItem) => ruleItem.uuid === manualRuleUuid);
  const userByUuid = useMemo(() => new Map(users.map((user) => [user.uuid, user])), [users]);
  const availableRecipientUsers = users.filter((user) => !recipientUserUuids.includes(user.uuid));
  const isPending = createEvent.isPending || runRule.isPending;

  useEffect(() => {
    if (!open) return;
    setManualRuleUuid(initialRuleUuid || '');
    setManualDryRun(true);
  }, [open, initialRuleUuid]);

  const handleClose = () => {
    if (!isPending) onClose();
  };

  const resetTestForm = () => {
    setForm({
      uuid_rule: '',
      title: '',
      message: '',
      dedupe_key: '',
      contract_number: '',
    });
    setSelectedRecipientUuid('');
    setRecipientUserUuids([]);
  };

  const handleAddRecipient = () => {
    if (!selectedRecipientUuid || recipientUserUuids.includes(selectedRecipientUuid)) return;
    setRecipientUserUuids((prev) => [...prev, selectedRecipientUuid]);
    setSelectedRecipientUuid('');
  };

  const handleRemoveRecipient = (uuidUser) => {
    setRecipientUserUuids((prev) => prev.filter((item) => item !== uuidUser));
  };

  const handleRunRule = () => {
    if (!manualRuleUuid) return;

    runRule.mutate(
      {
        uuid: manualRuleUuid,
        dryRun: manualDryRun,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const handleSubmit = () => {
    const recipients = recipientUserUuids.map((uuidUser) => {
      const user = userByUuid.get(uuidUser);
      return {
        recipient_type: 'internal_user',
        recipient_uuid: uuidUser,
        recipient_label: user?.names || user?.email || uuidUser,
        channel: 'internal_panel',
      };
    });

    createEvent.mutate(
      {
        uuid_rule: form.uuid_rule,
        title: form.title || selectedRule?.name,
        message: form.message || selectedRule?.description,
        dedupe_key: form.dedupe_key || undefined,
        contract_number: form.contract_number || undefined,
        payload: {
          target_app_module: 'habitat_99',
          source: 'manual_test',
          created_from: 'internal_notifications_inbox',
        },
        recipients,
      },
      {
        onSuccess: () => {
          resetTestForm();
          onClose();
        },
      }
    );
  };

  return (
    <DialogContainer
      open={open}
      onClose={handleClose}
      size="medium"
      minHeight="auto"
      disableScrollLock
    >
      <DialogTitle>Operaciones de notificaciones</DialogTitle>
      <DialogBody sx={{ display: 'block' }}>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Ejecución manual
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={7}>
                <CustomSelect
                  label="Notificación"
                  value={manualRuleUuid}
                  onChange={(event) => setManualRuleUuid(event.target.value)}
                  disabled={isPending}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return 'Seleccionar';
                    return rules.find((ruleItem) => ruleItem.uuid === value)?.name || value;
                  }}
                >
                  <MenuItem value="">Seleccionar</MenuItem>
                  {rules.map((ruleItem) => (
                    <MenuItem key={ruleItem.uuid} value={ruleItem.uuid}>
                      {ruleItem.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomSelect
                  label="Modo"
                  value={manualDryRun ? 'dry_run' : 'real'}
                  onChange={(event) => setManualDryRun(event.target.value === 'dry_run')}
                  disabled={isPending}
                >
                  <MenuItem value="dry_run">Dry-run</MenuItem>
                  <MenuItem value="real">Ejecución real</MenuItem>
                </CustomSelect>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ pt: 1.5 }}>
                  <CustomButton
                    variant="base"
                    onClick={handleRunRule}
                    disabled={!manualRuleUuid || isPending}
                    sx={{ width: '100%' }}
                  >
                    Ejecutar
                  </CustomButton>
                </Box>
              </Grid>
              {manualRule && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {manualRule.description || 'Sin descripción.'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Notificación de prueba
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomSelect
                  label="Regla"
                  value={form.uuid_rule}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, uuid_rule: event.target.value }))
                  }
                  required
                  disabled={isPending}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return 'Seleccionar';
                    return rules.find((ruleItem) => ruleItem.uuid === value)?.name || value;
                  }}
                >
                  <MenuItem value="">Seleccionar</MenuItem>
                  {rules.map((ruleItem) => (
                    <MenuItem key={ruleItem.uuid} value={ruleItem.uuid}>
                      {ruleItem.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Título"
                  placeholder={selectedRule?.name || 'Ej: Prueba de vencimiento de cupón'}
                  value={form.title}
                  onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                  fullWidth
                  disabled={isPending}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Clave idempotente"
                  placeholder="Opcional. Ej: prueba-vencimiento-2026-07-12"
                  value={form.dedupe_key}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, dedupe_key: event.target.value }))
                  }
                  fullWidth
                  disabled={isPending}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextArea
                  label="Mensaje"
                  placeholder={
                    selectedRule?.description || 'Escribí el mensaje que verá el usuario.'
                  }
                  value={form.message}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, message: event.target.value }))
                  }
                  minRows={3}
                  maxRows={6}
                  disabled={isPending}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Contrato"
                  placeholder="Opcional. Ej: 12345"
                  value={form.contract_number}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, contract_number: event.target.value }))
                  }
                  fullWidth
                  disabled={isPending}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start" sx={{ mt: 1.5 }}>
                  <CustomAutoComplete
                    label="Destinatario"
                    value={selectedRecipientUuid}
                    onChange={(event) => setSelectedRecipientUuid(event.target.value)}
                    disabled={usersLoading || isPending}
                    loading={usersLoading}
                    options={availableRecipientUsers}
                    optionValueKey="uuid"
                    placeholder="Buscar usuario"
                    forcePopupIcon={false}
                    noOptionsText="Sin usuarios disponibles"
                    getOptionLabel={(option) =>
                      option ? `${option.names || 'Usuario'} - ${option.email || option.uuid}` : ''
                    }
                  />
                  <Box>
                    <CustomButton
                      variant="base"
                      onClick={handleAddRecipient}
                      disabled={!selectedRecipientUuid || isPending}
                      sx={{ minWidth: 130 }}
                    >
                      Agregar
                    </CustomButton>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {recipientUserUuids.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Seleccioná al menos un usuario para enviar la prueba.
                    </Typography>
                  )}
                  {recipientUserUuids.map((uuidUser) => {
                    const user = userByUuid.get(uuidUser);
                    return (
                      <Chip
                        key={uuidUser}
                        label={user ? `${user.names} - ${user.email}` : uuidUser}
                        onDelete={() => handleRemoveRecipient(uuidUser)}
                        sx={{ mb: 0.5 }}
                      />
                    );
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Stack>
      </DialogBody>
      <MuiDialogActions sx={{ gap: 1.5, justifyContent: 'flex-end', px: 3, pb: 3 }}>
        <CustomButton variant="warning" onClick={handleClose} disabled={isPending}>
          Cerrar
        </CustomButton>
        <CustomButton
          variant="base"
          onClick={handleSubmit}
          disabled={!form.uuid_rule || recipientUserUuids.length === 0 || isPending}
        >
          Crear prueba
        </CustomButton>
      </MuiDialogActions>
    </DialogContainer>
  );
};
